import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react";
import type { WalkthroughConfig, WalkthroughProgress, WalkthroughState } from "./types";

interface WalkthroughContextValue extends WalkthroughState {
  startWalkthrough: (config: WalkthroughConfig) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipWalkthrough: () => void;
  finishWalkthrough: () => void;
  dismissWalkthrough: () => void;
  toggleHelpPanel: () => void;
  getProgress: (id: string) => WalkthroughProgress | undefined;
  resetProgress: (id: string) => void;
}

const STORAGE_KEY = "memberos_walkthrough_progress";

function loadProgress(): Record<string, WalkthroughProgress> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveProgress(progress: Record<string, WalkthroughProgress>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    /* ignored */
  }
}

const WalkthroughCtx = createContext<WalkthroughContextValue | null>(null);

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const [activeWalkthrough, setActiveWalkthrough] = useState<WalkthroughConfig | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState<Record<string, WalkthroughProgress>>(loadProgress);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);

  const updateProgress = useCallback((id: string, update: Partial<WalkthroughProgress>) => {
    setProgress((prev) => {
      const next = {
        ...prev,
        [id]: {
          ...prev[id],
          walkthroughId: id,
          currentStep: 0,
          status: "not_started" as const,
          ...prev[id],
          ...update,
        },
      };
      saveProgress(next);
      return next;
    });
  }, []);

  const startWalkthrough = useCallback(
    (config: WalkthroughConfig) => {
      setActiveWalkthrough(config);
      const existing = progress[config.id];
      const resumeStep = existing?.status === "in_progress" ? existing.currentStep : 0;
      setCurrentStep(resumeStep);
      updateProgress(config.id, {
        status: "in_progress",
        currentStep: resumeStep,
        startedAt: new Date().toISOString(),
      });
      setHelpPanelOpen(false);
    },
    [progress, updateProgress],
  );

  const nextStep = useCallback(() => {
    if (!activeWalkthrough) return;
    const next = currentStep + 1;
    if (next >= activeWalkthrough.steps.length) {
      updateProgress(activeWalkthrough.id, {
        status: "completed",
        completedAt: new Date().toISOString(),
        currentStep: next,
      });
      setActiveWalkthrough(null);
      setCurrentStep(0);
    } else {
      setCurrentStep(next);
      updateProgress(activeWalkthrough.id, { currentStep: next });
    }
  }, [activeWalkthrough, currentStep, updateProgress]);

  const prevStep = useCallback(() => {
    if (!activeWalkthrough || currentStep <= 0) return;
    const prev = currentStep - 1;
    setCurrentStep(prev);
    updateProgress(activeWalkthrough.id, { currentStep: prev });
  }, [activeWalkthrough, currentStep, updateProgress]);

  const skipWalkthrough = useCallback(() => {
    if (!activeWalkthrough) return;
    updateProgress(activeWalkthrough.id, { status: "skipped" });
    setActiveWalkthrough(null);
    setCurrentStep(0);
  }, [activeWalkthrough, updateProgress]);

  const finishWalkthrough = useCallback(() => {
    if (!activeWalkthrough) return;
    updateProgress(activeWalkthrough.id, { status: "completed", completedAt: new Date().toISOString() });
    setActiveWalkthrough(null);
    setCurrentStep(0);
  }, [activeWalkthrough, updateProgress]);

  const dismissWalkthrough = useCallback(() => {
    if (activeWalkthrough) {
      updateProgress(activeWalkthrough.id, { currentStep });
    }
    setActiveWalkthrough(null);
    setCurrentStep(0);
  }, [activeWalkthrough, currentStep, updateProgress]);

  const toggleHelpPanel = useCallback(() => setHelpPanelOpen((p) => !p), []);

  const getProgress = useCallback((id: string) => progress[id], [progress]);

  const resetProgress = useCallback((id: string) => {
    setProgress((prev) => {
      const next = { ...prev };
      delete next[id];
      saveProgress(next);
      return next;
    });
  }, []);

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      activeWalkthrough,
      currentStep,
      progress,
      helpPanelOpen,
      startWalkthrough,
      nextStep,
      prevStep,
      skipWalkthrough,
      finishWalkthrough,
      dismissWalkthrough,
      toggleHelpPanel,
      getProgress,
      resetProgress,
    }),
    [
      activeWalkthrough,
      currentStep,
      progress,
      helpPanelOpen,
      startWalkthrough,
      nextStep,
      prevStep,
      skipWalkthrough,
      finishWalkthrough,
      dismissWalkthrough,
      toggleHelpPanel,
      getProgress,
      resetProgress,
    ],
  );

  return <WalkthroughCtx.Provider value={value}>{children}</WalkthroughCtx.Provider>;
}

export function useWalkthrough() {
  const ctx = useContext(WalkthroughCtx);
  if (!ctx) throw new Error("useWalkthrough must be used within WalkthroughProvider");
  return ctx;
}
